import { textBox } from '../paint/text';
import type { Layer, Rect, Size } from './types';

/**
 * The one place that knows how big a layer is.
 *
 * Three implementations used to share this job with three different semantics:
 * `core/transforms.ts` ignored rotation and returned 0×0 for paint and text,
 * `core/move.ts` knew scale but not rotation, and `core/model/merge.ts`
 * rotated about the top-left corner while the engine, the export and the move
 * tool all rotate about the **centre** (design decision 6). Everything here
 * agrees with the engine:
 *
 *  - a layer's drawn size is its raster size × its scale;
 *  - rotation is about the drawn box's centre;
 *  - `layerAABB` is the axis-aligned box that contains the rotated result.
 *
 * `core/move.ts` keeps its own entry point because its callers inverse-rotate
 * points themselves and want the unrotated box — same numbers, same module.
 */

/**
 * The layer's intrinsic pixel size, in its own (unrotated, unscaled) space.
 *
 * Fill layers cover the document, so they need the document's size — pass it;
 * the shape and adjustment types have no pixels, and a fill layer asked about
 * without a document returns null for the same reason.
 */
export function layerNaturalSize(layer: Layer, doc?: Size): Size | null {
  switch (layer.type) {
    case 'image':
      return { width: layer.naturalWidth, height: layer.naturalHeight };
    case 'paint':
      return { width: layer.naturalWidth, height: layer.naturalHeight };
    case 'text':
      return textBox(layer);
    case 'fill':
      return doc ? { width: doc.width, height: doc.height } : null;
    default:
      // Shape layers have no raster until they are baked; adjustment layers
      // have no pixels of their own at all.
      return null;
  }
}

/** The layer's drawn size in document pixels: natural size × scale. */
export function layerDrawnSize(layer: Layer, doc?: Size): Size | null {
  const natural = layerNaturalSize(layer, doc);
  if (!natural) return null;
  return { width: natural.width * layer.scaleX, height: natural.height * layer.scaleY };
}

/** The layer's footprint before rotation: position plus drawn size. */
export function layerUnrotatedBounds(layer: Layer, doc?: Size): Rect | null {
  const drawn = layerDrawnSize(layer, doc);
  if (!drawn) return null;
  return { x: layer.x, y: layer.y, width: drawn.width, height: drawn.height };
}

/** The axis-aligned box containing the layer as drawn, rotation included. */
export function layerAABB(layer: Layer, doc?: Size): Rect | null {
  const bounds = layerUnrotatedBounds(layer, doc);
  if (!bounds) return null;
  return rotatedBounds(bounds, layer.angle);
}

/**
 * Bounding box after rotating clockwise about the rect's **centre**.
 *
 * Centre origin is what makes this agree with the engine, the export and the
 * move tool: a rotated layer's box rotates in place, so the centre of the
 * result is the centre of the input.
 */
export function rotatedBounds(rect: Rect, angle: number): Rect {
  if (!angle) return rect;
  const radians = (angle * Math.PI) / 180;
  const cos = Math.cos(radians);
  const sin = Math.sin(radians);
  const centreX = rect.x + rect.width / 2;
  const centreY = rect.y + rect.height / 2;
  const corners: [number, number][] = [
    [-rect.width / 2, -rect.height / 2],
    [rect.width / 2, -rect.height / 2],
    [rect.width / 2, rect.height / 2],
    [-rect.width / 2, rect.height / 2],
  ];
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const [dx, dy] of corners) {
    const x = centreX + dx * cos - dy * sin;
    const y = centreY + dx * sin + dy * cos;
    if (x < minX) minX = x;
    if (y < minY) minY = y;
    if (x > maxX) maxX = x;
    if (y > maxY) maxY = y;
  }
  return {
    x: minX,
    y: minY,
    width: maxX - minX,
    height: maxY - minY,
  };
}
